import type {
	ImportFileMappingFormData,
	ImportFromFileContextValue,
	ImportFromFileFormData,
} from '@/shared/components/ImportFromFileModal'
import type { ValidationError } from '@/shared/generated'

import React, { useMemo } from 'react'
import { entries, uniq } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useValidateProductDiffFileMutation } from '@/entities/assortment'
import { useColumnsSchemaQuery } from '@/entities/column'
import { useUpdateProductsFieldsMutation } from '@/entities/optimization'

import { client } from '@/shared/api/client'
import { ValidationErrorsList } from '@/shared/components/common/ValidationErrorsList'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	ImportFromFileErrors,
	ImportFromFileForm,
	ImportFromFileFormDescription,
	ImportFromFileFormFields,
	ImportFromFileMapping,
	ImportFromFileProcessing,
	ImportFromFileProvider,
	useImportFromFile,
} from '@/shared/components/ImportFromFileModal'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/ui/alert'
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { HiddenOnContext } from '@/shared/generated'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { ampli } from '@/shared/lib/ampli'
import { downloadProductsImportTemplate } from '@/shared/lib/download-xlsx'

type ProductsImportModalProps = {
	close(): void
	isOpen: boolean
}

const ImportFromFileModalContent = ({
	errors,
}: {
	errors?: ValidationError[] | null
}) => {
	const { t } = useTranslation()

	const { step } = useImportFromFile()
	const handleDownloadTemplate = async () => {
		const columns = await client.fieldSchema.fieldSchemaList({
			context: [HiddenOnContext.Assortment],
		})
		downloadProductsImportTemplate(
			uniq([
				'SKU',
				...columns
					.filter((column) => column.params.editable)
					.map((column) =>
						t(column.name, { ns: 'fields', defaultValue: column.name })
					),
			])
		)
	}

	return (
		<>
			{step === 'form' && (
				<ImportFromFileForm>
					<div className='flex items-center gap-1 text-sm text-muted-foreground'>
						<strong>1</strong>
						<span>
							{t('modal_import_og_will_be_updated', {
								defaultValue: 'optimization group(s) will be updated',
							})}
						</span>
					</div>
					<ImportFromFileFormFields />
					<ImportFromFileFormDescription
						onDownloadTemplate={handleDownloadTemplate}
					/>
				</ImportFromFileForm>
			)}
			{step === 'mapping' && <ImportFromFileMapping />}
			{step === 'processing' && <ImportFromFileProcessing />}
			{step === 'errors' && (
				<ImportFromFileErrors>
					{errors && Array.isArray(errors) && (
						<ValidationErrorsList errors={errors} />
					)}
					{errors && !Array.isArray(errors) && (
						<Alert variant='destructive'>
							<AlertTitle>{t('general_error_types')}:</AlertTitle>
							<AlertDescription>
								<div className='grid gap-2 text-sm'>
									<p>{errors}</p>
									<p>{t('general_possible_reasons')}:</p>
									<p
										className='ml-4'
										dangerouslySetInnerHTML={{
											__html: t('og_import_error_hint_html'),
										}}
									/>
								</div>
							</AlertDescription>
						</Alert>
					)}
				</ImportFromFileErrors>
			)}
		</>
	)
}

export function ProductsImportModal({
	close,
	isOpen,
}: ProductsImportModalProps) {
	const { t } = useTranslation()

	const optimizationGroupId = useOptimizationGroupId()!
	const updateProductsFields =
		useUpdateProductsFieldsMutation(optimizationGroupId)
	const validateProductDiffFile = useValidateProductDiffFileMutation()
	const columnsSchemaQuery = useColumnsSchemaQuery<
		{
			label: string
			value: string
		}[]
	>(HiddenOnContext.Products, {
		select: (data) =>
			data
				?.filter((column) => column.params.editable || column.id === 'sku')
				?.map((column) => ({
					label: t(column.name, { ns: 'fields', defaultValue: column.name }),
					value: column.id,
				})) || [],
	})
	const onSubmit = async (
		{
			product_diffs,
			ignore_empties,
			file_header_mapping,
		}: ImportFromFileFormData & ImportFileMappingFormData,
		context: ImportFromFileContextValue
	) => {
		ampli.tableImportFromFile({
			'Ignore empty fields': ignore_empties,
		})
		context.setStep('processing')
		try {
			const updates = await validateProductDiffFile.mutateAsync({
				product_diffs_file: product_diffs[0],
				ignore_empties,
				file_header_mapping: JSON.stringify(file_header_mapping),
			})
			if (updates.errors?.length) {
				context.setStep('errors')
			} else {
				await updateProductsFields.mutateAsync({
					changes: entries(updates.product_diffs as object).map(
						([key, value]) => ({
							sku: key,
							...value,
						})
					),
					ignore_empties: !!ignore_empties,
					file_header_mapping,
				})
				close()
				toast.success(t('toast.entity.updated', { entity: 'Products' }))
			}
		} catch (error) {
			context.setStep('errors')
		}
	}
	const errors = useMemo(() => {
		if (validateProductDiffFile.data?.errors) {
			return validateProductDiffFile.data?.errors
		}

		return null
	}, [validateProductDiffFile])

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ImportFromFileProvider
				onSubmit={onSubmit}
				schemaOptions={columnsSchemaQuery?.data || []}
			>
				<ModalContent data-testid='products-import-modal'>
					<ModalHeader>
						<ModalTitle>{t('modal_import_title')}</ModalTitle>
					</ModalHeader>
					<ErrorBoundary>
						<ImportFromFileModalContent errors={errors} />
					</ErrorBoundary>
				</ModalContent>
			</ImportFromFileProvider>
		</Modal>
	)
}
